import { create } from 'zustand';
import { getActiveSubscriptionSnapshot } from '@/features/subscription/model/active-subscription.query';
import { useLimitPaywallStore } from '@/shared/stores/limit-paywall-store';

/**
 * Which Pass the paywall opens highlighted.
 *
 * Post-pivot only `builder` is purchasable (Builder Pass — the contractor
 * subscription). `pro` / `designer` remain in the vocabulary because ~25
 * feature-gate call sites still request them, but they no longer open the
 * Pass sheet — see the redirect in `open()` below.
 */
export type PassPaywallHighlight = 'pro' | 'designer' | 'builder';

/**
 * App-wide presenter for the Pass paywall dialog.
 *
 * Mirrors `useLimitPaywallStore` + Apple's `PassPaywallPresenter`: the dialog
 * is mounted once at the root and any deep gate just calls `open(highlight)`.
 *
 * Facade Pack pivot policy (P3.1/P3.5): Pro/Designer Passes are retired from
 * sale (grandfathered holders keep their entitlements, and a holder's gates
 * simply never fire). The thing that unlocks pro-level features now is the
 * Facade Pack for the current building — so a locked-feature tap that used to
 * upsell a Pass routes to the PACK paywall instead, using the preflight
 * PACK_REQUIRED payload (server-owned copy, same payload a 422 would carry).
 * Centralized here so every legacy `open('pro'/'designer')` call site keeps
 * working without per-site rewiring; only `builder` opens the actual Pass
 * sheet (`GET /passes` serves the Builder Pass only post-pivot).
 */
interface PassPaywallState {
  highlight: PassPaywallHighlight | null;
  open: (highlight: PassPaywallHighlight) => void;
  close: () => void;
}

export const usePassPaywallStore = create<PassPaywallState>((set) => ({
  highlight: null,
  open: (highlight) => {
    if (highlight !== 'builder') {
      // `paywall_payloads` lives on the plan resource (real plan or guest
      // plan) — same non-hook snapshot read the limit-error router uses.
      const plan = getActiveSubscriptionSnapshot().plan;
      const attrs = plan && 'attributes' in plan ? plan.attributes : plan;
      const payload = attrs?.paywall_payloads?.PACK_REQUIRED;
      if (payload) {
        useLimitPaywallStore.getState().showPaywall(payload, 'PACK_REQUIRED');
        return;
      }
      // Cold start without a preflight map (shouldn't happen post-login):
      // fall through to the Pass sheet rather than swallowing the tap — it
      // shows a load-error state with a retry instead of dead UI.
    }
    set({ highlight });
  },
  close: () => set({ highlight: null }),
}));
